import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import { Shield, AlertCircle, Info, TrendingDown, Calculator } from 'lucide-react';
import { calcAntiDilution } from '@/utils/equityMath';

/**
 * Share of the pre-round capitalisation treated as common for the narrow-based
 * method. An assumption, not a figure derived from the cap table — the UI says so.
 */
const NARROW_BASE_PCT = 80;

interface CalculatorInputs {
  originalInvestment: number;
  originalPrice: number;
  originalShares: number;
  newRoundPrice: number;
  newRoundShares: number;
  totalSharesBeforeRound: number;
}

interface ProtectionResult {
  method: string;
  description: string;
  additionalShares: number;
  newTotalShares: number;
  adjustedPrice: number;
  ownershipPct: number;
  color: string;
}

export function AntiDilutionCalculator() {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    originalInvestment: 1000000,
    originalPrice: 10.0,
    originalShares: 100000,
    newRoundPrice: 5.0, // Down round
    newRoundShares: 500000,
    totalSharesBeforeRound: 1000000,
  });

  const [showResults, setShowResults] = useState(false);

  const isDownRound = inputs.newRoundPrice < inputs.originalPrice;

  const results = useMemo<ProtectionResult[]>(() => {
    if (!showResults || !isDownRound) return [];

    // The maths lives in calcAntiDilution so it can be tested. This component used
    // to carry its own copy of all four formulae.
    const META: Record<string, { description: string; color: string }> = {
      'No Protection': {
        description: 'Standard dilution - no anti-dilution protection',
        color: 'hsl(var(--destructive))',
      },
      'Full Ratchet': {
        description: 'Most investor-friendly - complete protection against down rounds',
        color: 'hsl(var(--chart-2))',
      },
      'Broad-Based Weighted Average': {
        description: 'Balanced approach - considers all outstanding shares',
        color: 'hsl(var(--primary))',
      },
      'Narrow-Based Weighted Average': {
        // The old description claimed this "only considers common stock". It does
        // not: it assumes 80% of the pre-round capitalisation is common, which is
        // an assumption, not a figure derived from the cap table.
        description: `More protection than broad-based - assumes ${NARROW_BASE_PCT}% of pre-round shares are common`,
        color: 'hsl(var(--chart-4))',
      },
    };

    return calcAntiDilution({ ...inputs, narrowBasePct: NARROW_BASE_PCT }).map((r) => ({
      method: r.method,
      description: META[r.method]?.description ?? '',
      additionalShares: r.additionalShares,
      newTotalShares: r.newTotalShares,
      adjustedPrice: r.adjustedPrice,
      ownershipPct: r.ownershipPct,
      color: META[r.method]?.color ?? 'hsl(var(--primary))',
    }));
  }, [inputs, showResults, isDownRound]);

  const handleCalculate = () => {
    setShowResults(true);
  };

  const chartData = results.map((r) => ({
    name: r.method,
    ownership: r.ownershipPct,
    additionalShares: r.additionalShares,
    color: r.color,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Anti-Dilution Protection Calculator
          <Badge variant="secondary" className="ml-auto">
            <Calculator className="h-3 w-3 mr-1" />
            Financial Model
          </Badge>
        </CardTitle>
        <CardDescription>
          Compare how different anti-dilution provisions protect your ownership in down rounds
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Educational Alert */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>What is Anti-Dilution Protection?</AlertTitle>
          <AlertDescription>
            Anti-dilution provisions protect preferred shareholders from ownership dilution when a
            company raises money at a lower valuation (down round). Different methods provide
            varying levels of protection, with Full Ratchet being the most investor-friendly and
            Weighted Average being more balanced.
          </AlertDescription>
        </Alert>

        <Tabs defaultValue="calculator" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="calculator">Calculator</TabsTrigger>
            <TabsTrigger value="comparison">Comparison</TabsTrigger>
            <TabsTrigger value="learn">Learn More</TabsTrigger>
          </TabsList>

          <TabsContent value="calculator" className="space-y-6">
            {/* Input Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Your Original Investment</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="originalInvestment">Investment Amount ($)</Label>
                  <Input
                    id="originalInvestment"
                    type="number"
                    value={inputs.originalInvestment}
                    onChange={(e) =>
                      setInputs({ ...inputs, originalInvestment: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="1000000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="originalPrice">Price per Share ($)</Label>
                  <Input
                    id="originalPrice"
                    type="number"
                    step="0.01"
                    value={inputs.originalPrice}
                    onChange={(e) =>
                      setInputs({ ...inputs, originalPrice: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="10.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="originalShares">Shares Owned</Label>
                  <Input
                    id="originalShares"
                    type="number"
                    value={inputs.originalShares}
                    onChange={(e) =>
                      setInputs({ ...inputs, originalShares: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="100000"
                  />
                </div>
              </div>

              <h3 className="text-lg font-semibold mt-6">New Funding Round (Down Round)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="newRoundPrice">New Price per Share ($)</Label>
                  <Input
                    id="newRoundPrice"
                    type="number"
                    step="0.01"
                    value={inputs.newRoundPrice}
                    onChange={(e) =>
                      setInputs({ ...inputs, newRoundPrice: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="5.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newRoundShares">Shares Being Issued</Label>
                  <Input
                    id="newRoundShares"
                    type="number"
                    value={inputs.newRoundShares}
                    onChange={(e) =>
                      setInputs({ ...inputs, newRoundShares: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="500000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totalSharesBeforeRound">Total Shares Outstanding</Label>
                  <Input
                    id="totalSharesBeforeRound"
                    type="number"
                    value={inputs.totalSharesBeforeRound}
                    onChange={(e) =>
                      setInputs({
                        ...inputs,
                        totalSharesBeforeRound: parseFloat(e.target.value) || 0,
                      })
                    }
                    placeholder="1000000"
                  />
                </div>
              </div>

              {!isDownRound && inputs.newRoundPrice > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    This is an up round (${inputs.newRoundPrice} {'>'} ${inputs.originalPrice}).
                    Anti-dilution protection typically doesn't apply to up rounds.
                  </AlertDescription>
                </Alert>
              )}

              {isDownRound && (
                <Alert>
                  <TrendingDown className="h-4 w-4" />
                  <AlertDescription>
                    Down round detected: ${inputs.newRoundPrice} is{' '}
                    {((1 - inputs.newRoundPrice / inputs.originalPrice) * 100).toFixed(1)}% lower
                    than your original price of ${inputs.originalPrice}
                  </AlertDescription>
                </Alert>
              )}

              <Button onClick={handleCalculate} disabled={!isDownRound} className="w-full">
                Calculate Protection Scenarios
              </Button>
            </div>

            {/* Results Section */}
            {showResults && results.length > 0 && (
              <div className="space-y-6 mt-8">
                <h3 className="text-lg font-semibold">Protection Comparison</h3>

                {/* Visual Chart */}
                <div className="border rounded-lg p-4">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-15} textAnchor="end" height={80} />
                      <YAxis label={{ value: 'Ownership %', angle: -90, position: 'insideLeft' }} />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-background border rounded-lg p-3 shadow-lg">
                                <p className="font-semibold">{data.name}</p>
                                <p className="text-sm">Ownership: {data.ownership.toFixed(2)}%</p>
                                <p className="text-sm">
                                  Additional Shares: {data.additionalShares.toLocaleString()}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="ownership" name="Ownership %">
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Detailed Table */}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Protection Method</TableHead>
                      <TableHead className="text-right">Additional Shares</TableHead>
                      <TableHead className="text-right">Total Shares</TableHead>
                      <TableHead className="text-right">Adjusted Price</TableHead>
                      <TableHead className="text-right">Ownership %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((result, index) => (
                      <TableRow
                        key={result.method}
                        className={
                          index === 0 ? 'bg-destructive/5' : index === 1 ? 'bg-chart-2/5' : ''
                        }
                      >
                        <TableCell>
                          <div>
                            <div className="font-medium">{result.method}</div>
                            <div className="text-xs text-muted-foreground">
                              {result.description}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {result.additionalShares > 0 ? '+' : ''}
                          {result.additionalShares.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {result.newTotalShares.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          ${result.adjustedPrice.toFixed(4)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge style={{ backgroundColor: result.color }} className="text-white">
                            {result.ownershipPct.toFixed(2)}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Key Insights */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Maximum Protection</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-green-600">
                        +{(results[1].ownershipPct - results[0].ownershipPct).toFixed(2)}%
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Full Ratchet provides the most protection, maintaining{' '}
                        {results[1].ownershipPct.toFixed(2)}% vs{' '}
                        {results[0].ownershipPct.toFixed(2)}% without protection
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Recommended: Weighted Average</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-primary">
                        {results[2].ownershipPct.toFixed(2)}%
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Broad-based weighted average offers balanced protection while being fair to
                        existing shareholders and future investors
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="comparison" className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Method</TableHead>
                  <TableHead>Investor Protection</TableHead>
                  <TableHead>Impact on Other Shareholders</TableHead>
                  <TableHead>Common Usage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Full Ratchet</TableCell>
                  <TableCell>
                    <Badge className="bg-green-500">Maximum</Badge>
                    <p className="text-xs mt-1">Complete protection against dilution</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="destructive">Severe</Badge>
                    <p className="text-xs mt-1">Very dilutive to common shareholders</p>
                  </TableCell>
                  <TableCell className="text-sm">
                    Rare - typically only in troubled companies or bridge rounds
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Broad-Based Weighted Average</TableCell>
                  <TableCell>
                    <Badge className="bg-blue-500">Moderate</Badge>
                    <p className="text-xs mt-1">Balanced protection based on all shares</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">Moderate</Badge>
                    <p className="text-xs mt-1">Fair impact distributed across all</p>
                  </TableCell>
                  <TableCell className="text-sm">Most common - standard in venture deals</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Narrow-Based Weighted Average</TableCell>
                  <TableCell>
                    <Badge className="bg-yellow-500">Good</Badge>
                    <p className="text-xs mt-1">More protection than broad-based</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="destructive">High</Badge>
                    <p className="text-xs mt-1">More dilutive to common stock holders</p>
                  </TableCell>
                  <TableCell className="text-sm">
                    Less common - favors preferred shareholders
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">No Protection</TableCell>
                  <TableCell>
                    <Badge variant="destructive">None</Badge>
                    <p className="text-xs mt-1">Full dilution in down rounds</p>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-green-500">None</Badge>
                    <p className="text-xs mt-1">Equal treatment for all shareholders</p>
                  </TableCell>
                  <TableCell className="text-sm">
                    Common stock and SAFEs typically have no protection
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="learn" className="space-y-4">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Full Ratchet</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  The investor's conversion price is adjusted down to the new round price. This
                  means they receive enough additional shares as if they had originally invested at
                  the lower price.
                </p>
                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-xs font-mono">
                    Additional Shares = (Original Investment / New Price) - Original Shares
                  </p>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  <strong>Example:</strong> If you invested $1M at $10/share (100k shares) and the
                  new round is $5/share, you'd receive 100k additional shares (as if you bought 200k
                  shares at $5 originally).
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2">Broad-Based Weighted Average</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  The conversion price is adjusted using a weighted average formula that considers
                  all outstanding shares (common, preferred, options, warrants). This provides
                  moderate protection while being fair to all stakeholders.
                </p>
                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-xs font-mono">
                    New Price = Old Price × [(A + B) / (A + C)]
                    <br />
                    A = Total shares before round
                    <br />
                    B = Money raised / Old Price
                    <br />C = New shares issued
                  </p>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  This is the most common method in venture capital deals as it balances investor
                  protection with fairness to other shareholders.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2">Narrow-Based Weighted Average</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Same formula as broad-based, but the base excludes options, warrants and other
                  unissued securities. This provides more protection to preferred shareholders at
                  the expense of common shareholders. Exactly what is excluded is a matter of
                  drafting, and this calculator does not read it from your cap table — it assumes{' '}
                  {NARROW_BASE_PCT}% of pre-round shares are common.
                </p>
                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-xs font-mono">
                    Same formula as broad-based, with A = {NARROW_BASE_PCT}% of pre-round shares
                  </p>
                </div>
              </div>

              <Alert className="mt-4">
                <Info className="h-4 w-4" />
                <AlertTitle>Important Notes</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside space-y-1 text-sm mt-2">
                    <li>
                      Anti-dilution protection typically only applies to preferred shares, not
                      common stock
                    </li>
                    <li>
                      Protection usually doesn't apply to employee option plans or certain strategic
                      issuances
                    </li>
                    <li>
                      Some provisions include a "pay-to-play" requirement to maintain protection
                    </li>
                    <li>
                      Anti-dilution adjustments affect all previous preferred rounds, not just your
                      investment
                    </li>
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
